import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, CheckCircle, TrendingUp, TrendingDown, Info, AlertTriangle } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

export default function AIInsights({ insights = [] }) {
  if (!insights || insights.length === 0) {
    return (
      <Card className="p-6 shadow-lg">
        <div className="text-center py-8">
          <Info className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Loading AI insights...</p>
        </div>
      </Card>
    );
  }

  const getInsightIcon = (type) => {
    switch (type) {
      case 'positive':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'negative':
        return <AlertCircle className="h-5 w-5 text-red-600" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-orange-600" />;
      default:
        return <Info className="h-5 w-5 text-blue-600" />;
    }
  };

  const getInsightColor = (type) => {
    switch (type) {
      case 'positive':
        return 'from-green-50 to-emerald-50 border-green-200';
      case 'negative':
        return 'from-red-50 to-rose-50 border-red-200';
      case 'warning':
        return 'from-orange-50 to-amber-50 border-orange-200';
      default:
        return 'from-blue-50 to-cyan-50 border-blue-200';
    }
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      high: 'bg-red-500',
      medium: 'bg-orange-500',
      low: 'bg-blue-500'
    };
    return colors[priority] || 'bg-gray-500';
  };

  return (
    <Card className="p-6 shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
          <TrendingUp className="h-5 w-5 mr-2 text-purple-600" />
          AI-Powered Insights
        </h3>
        <Badge className="bg-purple-600">Powered by AI</Badge>
      </div>

      <ScrollArea className="h-96">
        <div className="space-y-3">
          {insights.map((insight, index) => (
            <div 
              key={index} 
              className={`p-4 bg-gradient-to-br ${getInsightColor(insight.type)} rounded-lg border`}
              data-testid={`insight-${index}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  {getInsightIcon(insight.type)}
                  <h4 className="font-semibold text-gray-800">{insight.title}</h4>
                </div>
                <Badge className={getPriorityBadge(insight.priority)}>
                  {insight.priority}
                </Badge>
              </div>
              <p className="text-sm text-gray-700">{insight.message}</p>
            </div>
          ))}
        </div>
      </ScrollArea>
    </Card>
  );
}
